// ============================================================================
// Vendor Dashboard table columns. Same factory shape as the board's
// `buildColumns` (`../logistics/board_columns`) — the page hands in the handlers
// it owns and gets a column set back.
//
// Column set is the transporter's, not the board's: the ESCALATION column is
// always mounted (there are no sections here, so a flagged trip has nowhere else
// to carry its reason), LIVE ETA is new, and Owner / Carrier / Delhivery are
// dropped — an ops-desk assignment and a booking channel are not a transporter's
// business. Driver & vehicle is dropped too: every row sits under a trip panel
// whose heading already carries that trip's vehicle and driver, so the column only
// repeated it once per stop.
//
// The cells the board and this page render identically (escalation, dispatch,
// From / To) are IMPORTED from `board_columns` so the two can never drift. The
// customer cell is a deliberate copy: this page is the transporter's view of a
// delivery and that cell is the one phase 2 is most likely to change here
// (contact masking, per-stop sequence) without touching the board.
// ============================================================================
import { Button, Space, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import CopyText from "../components/copy_text";
import ExpressTag from "../components/express_tag";
import { LogisticsOrderRow } from "../types";
import { dash, dispatchLabel, floorLift, locationCell } from "../logistics/board_columns";
import MileTag from "./mile_tag";
import BreachTag from "./breach_tag";
import EscalationRaisedTag from "./escalation_tag";
import LiveEtaCell from "./live_eta";
import { DeliveryStatusTag } from "../logistics/components/logistics_status_tag";

// The customer this delivery is for — name (truncated) + the numbers the driver actually calls.
// A copy of the board's cell on purpose; see the header note.
const customerCell = (r: LogisticsOrderRow) => (
  <div style={{ minWidth: 0 }}>
    <Tooltip title={r.client_name ?? undefined}>
      <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {r.client_name || <span style={{ color: "#bbb" }}>—</span>}
      </div>
    </Tooltip>
    {r.client_contact && (
      <div style={{ fontSize: 12, color: "#888" }}>
        <CopyText value={r.client_contact} label="phone">
          {r.client_contact}
        </CopyText>
      </div>
    )}
    {r.client_alternate_contact && (
      <div style={{ fontSize: 12, color: "#888" }}>
        <CopyText value={r.client_alternate_contact} label="alternate phone">
          Alt: {r.client_alternate_contact}
        </CopyText>
      </div>
    )}
  </div>
);

// Whether LogiNext can show this booking at all. `provider_ref` is whatever the BOOKING's carrier
// calls it, so a Porter / Delhivery / intercity / supplier-managed truck has a reference LogiNext
// has never heard of — their API answers 200 with an empty body for one, and their page renders
// "Something went wrong" off that. Better to not offer tracking than to offer one that cannot work.
const trackable = (r: LogisticsOrderRow) => !!r.provider_ref && r.provider_type === "own_tms";

export interface VendorColumnHandlers {
  // booking id → live ETA (ISO), or null when LogiNext was asked and had none. Absent = not asked.
  liveEta: Record<number, string | null>;
  liveEtaLoading: boolean;
  liveEtaFailed: boolean;
  onSaved: (row: LogisticsOrderRow) => void;
  reload: () => void; // silent refetch after an action that returns nothing to merge
  onView: (r: LogisticsOrderRow) => void; // read-only details drawer
  onViewNotes: (r: LogisticsOrderRow) => void; // read-only escalation thread
  // Opens LogiNext's embedded tracking view for this booking. Only offered when the row has a
  // `provider_ref` — that IS their `ordno`, and their iFrame is keyed on it.
  onTrack: (r: LogisticsOrderRow) => void;
  // Whether this user may CLOSE an escalation (`oms.update`) — reading one is a wider right than
  // resolving it, so the "Mark resolved" link and the menu item are hidden without it. Same gate
}

export function buildVendorColumns(h: VendorColumnHandlers): ColumnsType<LogisticsOrderRow> {
  return [
    {
      // Allotment PO number(s) — a consolidated truck carries several, stacked. Click to copy.
      title: "PO #",
      // Wider than the board's: the weight line now also carries the escalation tag beside it.
      width: 200,
      key: "po",
      fixed: "left",
      render: (_, r) => {
        // Under the PO number(s): the weight, and to its right the escalation marker — which only
        // appears for an escalation raised on this trip's own dispatch date (see `escalation_tag`).
        // Rendered even when there is no PO, because a flagged truck must never be silent: without
        // this branch an unnumbered booking would drop the tag along with the dash.
        const footer = (
          <div
            style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 2 }}
          >
            {typeof r.cargo_weight_kg === "number" && r.cargo_weight_kg > 0 && (
              <span style={{ fontSize: 11, color: "#8c8c8c", whiteSpace: "nowrap" }}>
                {r.cargo_weight_kg} kg
              </span>
            )}
            <EscalationRaisedTag row={r} />
          </div>
        );
        // Tracking sits HERE rather than in the Action menu: it is the one thing a transporter
        // opens per stop, and two clicks behind "More" is one too many. Same gate as before — see
        // `trackable` for why a non-LogiNext booking is not offered it at all.
        return (
          <div>
            {/* Not `flex: 1` on the PO block — the link hugs the number instead of being pushed
                out to the far edge of the column. */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                {r.po_numbers?.length
                  ? r.po_numbers.map((po) => (
                      <CopyText key={po} value={po} label="PO number">
                        <div style={{ fontWeight: 500, whiteSpace: "nowrap", color: "#1677ff" }}>
                          {po}
                        </div>
                      </CopyText>
                    ))
                  : dash(null)}
              </div>
              {trackable(r) && (
                <Tooltip title="Track on LogiNext">
                  <Button
                    size="small"
                    type="link"
                    // The row itself opens the items drawer; tracking must not also do that.
                    onClick={(e) => {
                      e.stopPropagation();
                      h.onTrack(r);
                    }}
                    style={{ padding: 0, height: "auto", fontSize: 12, flex: "none" }}
                  >
                    Track
                  </Button>
                </Tooltip>
              )}
            </div>
            {footer}
          </div>
        );
      },
    },
    {
      title: "Sales order",
      key: "order",
      width: 200,
      render: (_, r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ whiteSpace: "nowrap" }}>
            {r.sales_order_id ? (
              <Tooltip title="Click to copy SO number">
                <CopyText
                  value={r.zoho_salesorder_number || String(r.sales_order_id)}
                  label="SO number"
                >
                  <span style={{ color: "#1677ff" }}>
                    {r.zoho_salesorder_number || `SO #${r.sales_order_id}`}
                  </span>
                </CopyText>
              </Tooltip>
            ) : (
              dash(r.zoho_salesorder_number)
            )}
          </div>
          {/* The mile tag is ALWAYS rendered (every row has a lane) — unlike the board, this page
              merges both halves, so nothing else on the row says which one it is. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 3 }}>
            <MileTag lane={r.board_lane} />
            <ExpressTag show={r.is_express} />
          </div>
          {/* Floor + lift, under the tags — the same line the board shows, and the thing the driver
              needs to know before arriving. Only rendered for an unloading customer delivery. */}
          {floorLift(r)}
        </div>
      ),
    },
    {
      // Target dispatch day + its time window — the same cell the board shows, and the axis the
      // date filter above works on.
      title: "Dispatch date",
      key: "dispatch",
      width: 110,
      render: (_, r) => dispatchLabel(r),
    },
    {
      // The physical delivery status ALONE. The overlays the board stacks here (attention, SLA,
      // escalation) are deliberately not shown: a transporter reads this column to know where the
      // goods are, and "Awaiting proof" / "SLA breached" answer a different question. Lateness
      // still has a column of its own — Breach, two across.
      title: "Status",
      key: "status",
      width: 130,
      render: (_, r) => (
        <Space size={[4, 4]} wrap>
          <DeliveryStatusTag status={r.delivery_status} />
        </Space>
      ),
    },
    {
      // The delivery SLA verdict — On time / Breached, with the rule and the size of the miss on
      // hover (see `breach.ts`). Sits next to Status because it answers the question Status raises:
      // "in transit" is fine at noon and a breach at seven. The `sla_state` overlay on Status is a
      // DIFFERENT clock (the leg's own due date, set at routing) and is deliberately left alone.
      title: "Breach",
      key: "breach",
      width: 110,
      render: (_, r) => <BreachTag row={r} />,
    },
    {
      // Pickup — label truncated, linking to the precise pin we store.
      title: "From",
      key: "from",
      width: 150,
      render: (_, r) => locationCell(r.origin_label, r.origin_lat, r.origin_long),
    },
    {
      // Drop — label truncated, linking to the precise pin we store.
      title: "To",
      key: "to",
      width: 150,
      render: (_, r) => locationCell(r.destination_label, r.destination_lat, r.destination_long),
    },
    {
      title: "Customer",
      key: "customer",
      width: 160,
      render: (_, r) => customerCell(r),
    },
    {
      // LIVE from LogiNext, fetched after the board loads (see `LiveEtaCell` for the two sources and
      // why the planned ETA is labelled rather than passed off as live).
      title: (
        <Tooltip title="LogiNext's live ETA — the same figure the customer sees on their tracking page. Falls back to the planned ETA from the trip plan, marked as such.">
          <span style={{ cursor: "help" }}>Estimated delivery time</span>
        </Tooltip>
      ),
      key: "live_eta",
      width: 150,
      render: (_, r) => <LiveEtaCell
          row={r}
          live={h.liveEta}
          loading={h.liveEtaLoading}
          failed={h.liveEtaFailed}
        />,
    },
  ];
}
